import * as React from "react";
import { BarChart3, Loader2, Trophy, Target, TrendingUp, TrendingDown } from "lucide-react";
import { cn } from "../../lib/utils";
import {
  Card,
  CardHeader,
  CardContent,
  CardTitle,
} from "./card";
import { PercentValue } from "./value-display";
import { Text, Label } from "./typography";
import { Badge } from "./badge";

/**
 * ETFComparison Component
 * Compares portfolio performance against multiple benchmarks:
 * - S&P 500 (US Stocks)
 * - NASDAQ (Tech)
 * - Bitcoin (Crypto)
 * - Gold (Commodities)
 *
 * @param {Object} props
 * @param {Object} props.data - ETF comparison data from API
 * @param {boolean} props.loading - Loading state
 */
export function ETFComparison({ data, loading = false }) {
  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-2 pt-3 px-4">
          <div className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-primary" />
            <CardTitle className="text-sm font-medium">Benchmark Comparison</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-3">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.message) {
    return (
      <Card>
        <CardHeader className="pb-2 pt-3 px-4">
          <div className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-primary" />
            <CardTitle className="text-sm font-medium">Benchmark Comparison</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-3">
          <Text variant="secondary" size="sm" className="text-center py-4">
            {data?.message || "Insufficient data for comparison. Need at least 2 snapshots with benchmark data."}
          </Text>
        </CardContent>
      </Card>
    );
  }

  const { portfolioReturn, benchmarks, startDate, endDate, dataPoints } = data;

  // Get benchmark data with fallback for older API format
  const benchmarkList = benchmarks ? [
    { key: 'sp500', ...benchmarks.sp500, color: 'bg-blue-500' },
    { key: 'nasdaq', ...benchmarks.nasdaq, color: 'bg-purple-500' },
    { key: 'bitcoin', ...benchmarks.bitcoin, color: 'bg-amber-500' },
    { key: 'gold', ...benchmarks.gold, color: 'bg-yellow-500' },
  ] : [
    // Fallback for legacy API format (only S&P 500)
    {
      key: 'sp500',
      name: 'S&P 500',
      return: data.sp500Return || 0,
      hasData: data.sp500Return !== 0 || (data.startSP500 && data.endSP500),
      outperformance: data.outperformance || 0,
      color: 'bg-blue-500',
    },
  ];

  // Filter to only benchmarks with data
  const availableBenchmarks = benchmarkList.filter(b => b.hasData);

  // Calculate relative scale for progress bars
  const allReturns = [portfolioReturn, ...availableBenchmarks.map(b => b.return)];
  const maxReturn = Math.max(...allReturns.map(Math.abs), 1);
  const getBarWidth = (value) => {
    const percentage = (Math.abs(value) / maxReturn) * 100;
    return Math.min(percentage, 100);
  };

  // Count wins/losses against benchmarks
  const wins = availableBenchmarks.filter(b => portfolioReturn > b.return).length;
  const total = availableBenchmarks.length;

  // Format date range
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const dateRange = startDate && endDate
    ? `${formatDate(startDate)} - ${formatDate(endDate)}`
    : "";

  return (
    <Card>
      <CardHeader className="pb-2 pt-3 px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-primary" />
            <CardTitle className="text-sm font-medium">Benchmark Comparison</CardTitle>
          </div>
          {availableBenchmarks.length > 0 && (
            <Badge
              variant={wins > total / 2 ? "success" : wins === total / 2 ? "secondary" : "destructive"}
              size="sm"
            >
              {wins > total / 2 ? (
                <Trophy className="h-3 w-3 mr-1" />
              ) : (
                <Target className="h-3 w-3 mr-1" />
              )}
              {wins}/{total} Beating
            </Badge>
          )}
        </div>
        {dateRange && (
          <Text variant="secondary" size="xs" className="mt-1">
            {dateRange} ({dataPoints} data points)
          </Text>
        )}
      </CardHeader>
      <CardContent className="px-4 pb-3 space-y-3">
        {/* Portfolio return - always first */}
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-primary" />
              <Label className="text-xs font-medium">Your Portfolio</Label>
            </div>
            <PercentValue
              value={portfolioReturn}
              size="sm"
              showTrend
            />
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div
              className={cn(
                "h-full rounded-full transition-all duration-500",
                portfolioReturn >= 0 ? "bg-primary" : "bg-destructive"
              )}
              style={{
                width: `${getBarWidth(portfolioReturn)}%`,
              }}
            />
          </div>
        </div>

        {/* Benchmark bars */}
        {availableBenchmarks.map((benchmark) => {
          const isBeating = portfolioReturn > benchmark.return;
          return (
            <div key={benchmark.key} className="space-y-1">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={cn("w-2 h-2 rounded-full", benchmark.color)} />
                  <Label className="text-xs">{benchmark.name}</Label>
                  {isBeating ? (
                    <TrendingUp className="h-3 w-3 text-success" />
                  ) : (
                    <TrendingDown className="h-3 w-3 text-destructive" />
                  )}
                </div>
                <PercentValue
                  value={benchmark.return}
                  size="sm"
                  showTrend
                />
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className={cn(
                    "h-full rounded-full transition-all duration-500",
                    benchmark.return >= 0 ? benchmark.color : "bg-destructive"
                  )}
                  style={{
                    width: `${getBarWidth(benchmark.return)}%`,
                  }}
                />
              </div>
            </div>
          );
        })}

        {/* No benchmarks available message */}
        {availableBenchmarks.length === 0 && (
          <div className="rounded-md p-3 border border-border bg-muted/50">
            <Text variant="secondary" size="xs">
              Benchmark data will be available once daily snapshots include market prices.
            </Text>
          </div>
        )}

        {/* Summary - show best/worst comparison */}
        {availableBenchmarks.length > 0 && (
          <div className="pt-2 border-t border-border">
            <div className="grid grid-cols-2 gap-2 text-xs">
              {/* Best outperformance */}
              {(() => {
                const bestOutperformance = availableBenchmarks
                  .filter(b => portfolioReturn > b.return)
                  .sort((a, b) => (portfolioReturn - a.return) - (portfolioReturn - b.return))
                  .pop();

                if (bestOutperformance) {
                  return (
                    <div className="flex items-center gap-1">
                      <TrendingUp className="h-3 w-3 text-success" />
                      <span className="text-muted-foreground">Best vs</span>
                      <span className="font-medium">{bestOutperformance.name}</span>
                      <PercentValue
                        value={portfolioReturn - bestOutperformance.return}
                        size="xs"
                        showSign
                        colorize={false}
                        className="text-success"
                      />
                    </div>
                  );
                }
                return null;
              })()}

              {/* Worst underperformance */}
              {(() => {
                const worstUnderperformance = availableBenchmarks
                  .filter(b => portfolioReturn <= b.return)
                  .sort((a, b) => (portfolioReturn - b.return) - (portfolioReturn - a.return))
                  .pop();

                if (worstUnderperformance) {
                  return (
                    <div className="flex items-center gap-1">
                      <TrendingDown className="h-3 w-3 text-destructive" />
                      <span className="text-muted-foreground">Worst vs</span>
                      <span className="font-medium">{worstUnderperformance.name}</span>
                      <PercentValue
                        value={portfolioReturn - worstUnderperformance.return}
                        size="xs"
                        showSign
                        colorize={false}
                        className="text-destructive"
                      />
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default ETFComparison;
